"use client";

import * as React from "react";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import { cn } from "../../lib/utils";
import {
	isShown,
	MenuItem,
	RowButton,
	RowMenu,
	sizedIcon,
	useAsyncSelect,
	withDisabledReason,
	type ActionDescriptorBase,
	type DescribedButtonAction,
	type DescribedMenuAction,
	type DescribedMenuItem,
} from "./action-descriptors";
import { ActionsMenu } from "./actions-menu";
import { Button } from "./button";
import { DropdownMenuItem } from "./dropdown-menu";
import { Breakpoint, useBreakpoint } from "./responsive";

/* -------------------------------------------------------------------------------------------------
 * Action descriptors
 *
 * The slots below are deliberately narrow. A descriptor carries what an action *is*, never how it
 * looks: there is no `variant`, `size` or `className`, so a caller cannot reach past the agreed
 * header rules to restyle a control. Anything that must vary by record state is expressed as
 * `isVisible` / `disabled` and resolved by the caller before it reaches the bar.
 * -----------------------------------------------------------------------------------------------*/

type HeaderPanelActionBase = ActionDescriptorBase;

/** An item inside a menu — the overflow menu, or a `download` / `add` action's dropdown. */
type HeaderPanelMenuItem = DescribedMenuItem;

/** A work action: Recalculate, Regenerate, Replace XLSX, Send test email. */
type HeaderPanelButtonAction = DescribedButtonAction & {
	type: "button";
};

/**
 * A work action that navigates to a related record: View redemption batch. Renders as the same grey
 * outline button as `button`, but as a real link through the bar's `linkAs`, so it can be opened in
 * a new tab. Folds into the overflow menu as a link item.
 */
type HeaderPanelLinkAction = ActionDescriptorBase & {
	type: "link";
	/** Required: side-by-side buttons need an icon to be told apart at a glance. */
	icon: React.ReactNode;
	href: string;
};

/**
 * One of the two stable menus.
 *
 * `download` collects exports. A single visible export is not worth a dropdown, so it renders as a
 * direct button with a download icon instead; two or more render as `Download ▾`.
 *
 * `add` collects "create a related thing" actions and keeps its dropdown even with one item, so the
 * affordance does not move around as a record gains and loses options.
 */
type HeaderPanelMenuAction = DescribedMenuAction;

type HeaderPanelAction =
	| HeaderPanelButtonAction
	| HeaderPanelLinkAction
	| HeaderPanelMenuAction;

/**
 * The component a `link` action renders through. It receives `href`, `className`, `children`, a ref
 * and the button's ARIA and data attributes, and must forward them to its anchor — the way Next's
 * `Link` does.
 */
type HeaderPanelLinkComponent = React.ElementType<{
	href: string;
	className?: string;
	children?: React.ReactNode;
}>;

/**
 * The single action that advances the record's state: Confirm, Approve, Mark as completed.
 *
 * Singular by type, so a header cannot grow a second filled button, and text-only as a button —
 * `menuIcon` is used solely for the folded form, where every menu item must be icon-led. A record
 * whose state cannot advance omits the primary entirely.
 */
type HeaderPanelPrimaryAction = HeaderPanelActionBase & {
	onSelect: () => void | Promise<unknown>;
	/** Icon for the folded menu item only. The button itself never renders an icon. */
	menuIcon: React.ReactNode;
	/**
	 * Swaps the label for "Processing..." and shows a spinner at the button's natural width. Only
	 * needed when the work outlives `onSelect`'s promise — a returned promise does this on its own.
	 */
	loading?: boolean;
};

type HeaderPanelActionBarProps = {
	/** Work actions and the two stable menus, rendered left to right in array order. */
	actions?: HeaderPanelAction[];
	primary?: HeaderPanelPrimaryAction;
	/** "Manage the record" items. Edit details belongs first. */
	manage?: HeaderPanelMenuItem[];
	/** Delete / Cancel / Revert. Always last in the menu, never a button in the row. */
	destructive?: HeaderPanelMenuItem[];
	/**
	 * Items that only make sense once the row has collapsed — the labels grid toggle, for one.
	 * Ignored at or above `foldBelow`.
	 */
	foldedExtras?: HeaderPanelMenuItem[];
	/** Viewport below which the whole row collapses into the overflow menu. */
	foldBelow?: Breakpoint;
	menuAriaLabel?: string;
	menuTestId?: string;
	/**
	 * Renders a `link` action. Pass the app's router link (admin-app's `OrgLink`), so navigation
	 * stays client-side and org-scoped. Defaults to a plain anchor.
	 */
	linkAs?: HeaderPanelLinkComponent;
	className?: string;
};

/** A folded `link` action: a menu item that navigates rather than runs a handler. */
type FoldedLinkItem = Omit<
	HeaderPanelLinkAction,
	"type" | "disabled" | "disabledReason"
>;

/** What the overflow menu renders: menu items, plus any `link` actions folded into it. */
type OverflowMenuEntry = HeaderPanelMenuItem | FoldedLinkItem;

/** Rule 6: one primary, up to four other controls, plus the overflow menu. */
const MAX_ROW_ACTIONS = 4;

const PROCESSING_LABEL = "Processing...";

const BREAKPOINT_ORDER = [
	Breakpoint.XS,
	Breakpoint.SM,
	Breakpoint.MD,
	Breakpoint.LG,
	Breakpoint.XL,
] as const;

function isBelowBreakpoint(current: Breakpoint, target: Breakpoint): boolean {
	return BREAKPOINT_ORDER.indexOf(current) < BREAKPOINT_ORDER.indexOf(target);
}

function PrimaryButton({ action }: { action: HeaderPanelPrimaryAction }) {
	const { pending, run } = useAsyncSelect(action.onSelect);
	const isProcessing = action.loading || pending;

	return withDisabledReason(
		<Button
			variant="primary"
			size="sm"
			loading={isProcessing}
			disabled={action.disabled}
			onClick={run}
			aria-label={action["aria-label"]}
			data-testid={action["data-testid"]}
		>
			{isProcessing ? PROCESSING_LABEL : action.label}
		</Button>,
		action,
	);
}

function RowLink({
	action,
	linkAs: LinkComponent,
}: {
	action: HeaderPanelLinkAction;
	linkAs: HeaderPanelLinkComponent;
}) {
	// A disabled link stays focusable and still follows on Enter, so it renders as a button instead.
	if (action.disabled) {
		return <RowButton action={{ ...action, onSelect: () => undefined }} />;
	}

	return (
		<Button
			asChild
			variant="outline"
			size="sm"
			aria-label={action["aria-label"]}
			data-testid={action["data-testid"]}
		>
			<LinkComponent href={action.href}>
				{sizedIcon(action.icon, "size-3.5")}
				{action.label}
			</LinkComponent>
		</Button>
	);
}

function LinkMenuItem({
	item,
	linkAs: LinkComponent,
}: {
	item: FoldedLinkItem;
	linkAs: HeaderPanelLinkComponent;
}) {
	return (
		<DropdownMenuItem
			asChild
			className="cursor-pointer"
			aria-label={item["aria-label"]}
			data-testid={item["data-testid"]}
		>
			<LinkComponent href={item.href}>
				<span className="mr-2">{sizedIcon(item.icon, "size-3.5")}</span>
				{item.label}
			</LinkComponent>
		</DropdownMenuItem>
	);
}

/** Turns a row action into the menu entries it contributes once the row has folded. */
function foldAction(action: HeaderPanelAction): OverflowMenuEntry[] {
	if (action.type === "link") {
		// Disabled, it folds to a disabled item that goes nowhere — the same reason a disabled row
		// link renders as a button.
		if (action.disabled) {
			return [
				{
					label: action.label,
					icon: action.icon,
					onSelect: () => undefined,
					disabled: true,
					disabledReason: action.disabledReason,
					"aria-label": action["aria-label"],
					"data-testid": action["data-testid"],
				},
			];
		}
		return [
			{
				label: action.label,
				icon: action.icon,
				href: action.href,
				"aria-label": action["aria-label"],
				"data-testid": action["data-testid"],
			},
		];
	}
	// A menu has no trigger to fold, so it contributes its items directly.
	if (action.type !== "button") {
		return action.items.filter(isShown).map((item) =>
			action.disabled
				? {
						...item,
						disabled: true,
						disabledReason: item.disabledReason ?? action.disabledReason,
					}
				: item,
		);
	}
	return [
		{
			label: action.label,
			icon: action.icon,
			onSelect: action.onSelect,
			disabled: action.disabled,
			disabledReason: action.disabledReason,
			"aria-label": action["aria-label"],
			"data-testid": action["data-testid"],
		},
	];
}

function foldPrimary(action: HeaderPanelPrimaryAction): HeaderPanelMenuItem {
	return {
		label: action.loading ? PROCESSING_LABEL : action.label,
		icon: action.menuIcon,
		onSelect: action.onSelect,
		disabled: action.disabled || action.loading,
		disabledReason: action.disabledReason,
		"aria-label": action["aria-label"],
		"data-testid": action["data-testid"],
	};
}

function warnOnRowActionCap(count: number): void {
	if (process.env.NODE_ENV !== "production" && count > MAX_ROW_ACTIONS) {
		// eslint-disable-next-line no-console -- no logger context in packages/ui
		console.warn(
			`HeaderPanelActionBar: ${count} row actions exceeds the cap of ${MAX_ROW_ACTIONS}. ` +
				"Move the least-used actions into `manage` so the header stays readable.",
		);
	}
}

/**
 * The action row for a record header. `actions` render left to right in caller-provided order, then
 * the state-advancing primary, then the overflow menu — always 8px apart. Callers must supply work
 * actions, then the two stable menus; the bar does not reorder those descriptors.
 *
 * Below `foldBelow` the entire row collapses into the overflow menu, which becomes the only control
 * on screen. Folding is computed from one breakpoint read and rendered down one path, so an action
 * is declared once and cannot drift between its wide and narrow forms.
 */
function HeaderPanelActionBar({
	actions = [],
	primary,
	manage = [],
	destructive = [],
	foldedExtras = [],
	foldBelow = Breakpoint.LG,
	menuAriaLabel = "More actions",
	menuTestId,
	linkAs = "a",
	className,
}: HeaderPanelActionBarProps) {
	const breakpoint = useBreakpoint();
	const isFolded = isBelowBreakpoint(breakpoint, foldBelow);

	const visibleActions = actions.filter(isShown);
	const visiblePrimary = primary && isShown(primary) ? primary : undefined;

	warnOnRowActionCap(visibleActions.length);

	const rowActions = isFolded ? [] : visibleActions;
	const rowPrimary = isFolded ? undefined : visiblePrimary;

	const menuItems: OverflowMenuEntry[] = isFolded
		? [
				...(visiblePrimary ? [foldPrimary(visiblePrimary)] : []),
				...visibleActions.flatMap(foldAction),
				...manage,
				...foldedExtras,
				...destructive,
			]
		: [...manage, ...destructive];

	const visibleMenuItems = menuItems.filter(isShown);

	return (
		<div
			data-slot="header-panel-action-bar"
			className={cn("flex items-center gap-2", className)}
		>
			{withOccurrenceKeys(
				rowActions,
				(action) => `${action.type}:${action["data-testid"] ?? action.label}`,
			).map(({ item: action, key }) => {
				switch (action.type) {
					case "button":
						return <RowButton key={key} action={action} />;
					case "link":
						return <RowLink key={key} action={action} linkAs={linkAs} />;
					case "download":
					case "add":
						return <RowMenu key={key} action={action} />;
				}
			})}
			{rowPrimary && <PrimaryButton action={rowPrimary} />}
			{visibleMenuItems.length > 0 && (
				<ActionsMenu>
					<ActionsMenu.Trigger
						aria-label={menuAriaLabel}
						data-testid={menuTestId}
					/>
					<ActionsMenu.Content>
						{withOccurrenceKeys(
							visibleMenuItems,
							(item) => item["data-testid"] ?? item.label,
						).map(({ item, key }) =>
							"href" in item ? (
								<LinkMenuItem key={key} item={item} linkAs={linkAs} />
							) : (
								<MenuItem key={key} item={item} />
							),
						)}
					</ActionsMenu.Content>
				</ActionsMenu>
			)}
		</div>
	);
}

export { HeaderPanelActionBar, MAX_ROW_ACTIONS };

export type {
	HeaderPanelActionBarProps,
	HeaderPanelAction,
	HeaderPanelButtonAction,
	HeaderPanelLinkAction,
	HeaderPanelLinkComponent,
	HeaderPanelMenuAction,
	HeaderPanelMenuItem,
	HeaderPanelPrimaryAction,
};
